// Pulsed review (dolphin clicks & bursts, sperm whales): what the person can change on a 10 s piece and what is
// recomputed from it (DISPLAY_CATALOGUE P9: "edits are stored separately from the engine's proposal and every
// downstream measure — trains, ICI, rhythm, animals, counts — is recomputed"). Light recompute here; the heavy one
// (re-running the train finder on the edited clicks) is the engine's job on the final review.
import { localisationDoc } from './Localisation';
import { sectionForSpecies, speciesName } from './sections';
import { amin } from './safeMath';

export function median(a) {
  const v = a.filter((x) => x !== null && x !== undefined && Number.isFinite(x)).sort((p, q) => p - q);
  return v.length ? v[Math.floor(v.length / 2)] : null;
}

export const PULSED_SCHEMA = 'cetalingua.pulsedReview/1';
// label colours = the engine's (figures.LABEL_COLOURS), so every view matches the engine's figures
export const LABEL = {
  sequence: ['Click in a train', '#1f6fd1'],
  chorus: ['Chorus click', '#17becf'],
  added: ['Added by you', '#000000'],
  unassigned: ['Single click (no train)', '#8e44ad'],
  echo: ['Echo / other arrival of a click', '#9a9a9a'],
  // "shrimp" = a pulse in no train and not an echo (leftover class, engine 8 Oct 2026) — usually a snap
  shrimp: ['Isolated pulse (shrimp?)', '#c0392b'],
  uncertain: ['Uncertain', '#e08a00'],
  interference: ['Interference', '#2e9e5b'],
  machinery: ['Machinery', '#5a6b7a'],
  vessel: ['Vessel', '#8c564b'],
};
export const ANIMAL_LABELS = new Set(['sequence', 'chorus', 'added', 'unassigned']);
export const RELABEL = ['sequence', 'unassigned', 'echo', 'shrimp', 'uncertain', 'interference', 'vessel'];
export const ANIMAL_COLORS = ['#58a6ff', '#ff7b72', '#3fb950', '#d2a8ff', '#ffa657', '#56d4dd', '#f778ba', '#e3b341'];
const r3 = (x) => Math.round(x * 1000) / 1000;

export function fromPiece(entry) {
  return {
    removed: [], added: [], relabel: {}, train: {},             // clicks: removed click_no, added {id,t,f,train}, label / train changes
    trainType: {},                                                 // train id -> 'click train' | 'burst pulse' (new groups / retyped)
    bursts: { unmarked: [], marked: [] },                          // engine burst groups unmarked; bursts marked by the person
    creaks: [], codas: [],                                         // time ranges marked by the person
    loc: {},                                                       // click_no -> 'accepted' | 'rejected' (positions; only changes)
    label: { code: entry.code || '', speciesId: entry.speciesId || null, speciesLabel: entry.species || null, broad: '' },
    openedAt: Date.now(),
  };
}

/** The clicks as they are now: engine clicks minus removed, with label / train changes, plus added ones. */
export function currentClicks(entry, st) {
  const c = entry.clicks; const n = c.t.length;
  const removed = new Set(st.removed);
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const no = c.no[i];
    if (removed.has(no)) continue;
    const label = st.relabel[no] || c.label[i];
    const train = no in st.train ? st.train[no] : c.train[i];
    out.push({ i, no, t: c.t[i], f: c.peak?.[i] ?? null, label, train: train === undefined || train === null || train < 0 ? null : train,
      animal: c.animal?.[i] ?? null, ind: c.ind?.[i] ?? null, pp: c.pp?.[i] ?? null, level: c.level?.[i] ?? c.peakdb?.[i] ?? null,
      ipi: c.ipi?.[i] ?? null, echoDelay: c.echoDelay?.[i] ?? null, echoOf: c.echoOf?.[i] ?? null,
      origin: (st.relabel[no] || no in st.train) ? 'adjusted' : 'engine' });
  }
  // clicks the reviewer added: measured in the browser from the band-passed signal (same maths as the click card)
  st.added.forEach((a) => out.push({ i: null, no: a.id, t: a.t, f: a.meas?.peak ?? a.f, label: 'added', train: a.train ?? null, animal: null, ind: null,
    pp: a.meas?.pp ?? null, level: a.meas?.peakdb ?? null, meas: a.meas || null, auto: !!a.auto, origin: 'added' }));
  return out.sort((a, b) => a.t - b.t);
}

// engine (pulsed/labels.py): Pearson correlation of the intervals with their order; > 0.5 rising, < -0.5 falling
function trend(ici) {
  if (ici.length < 3) return 'steady';
  const n = ici.length; const mx = (n - 1) / 2; const my = ici.reduce((p, q) => p + q, 0) / n;
  let sxy = 0; let sxx = 0; let syy = 0; ici.forEach((v, j) => { sxy += (j - mx) * (v - my); sxx += (j - mx) ** 2; syy += (v - my) ** 2; });
  if (!sxx || !syy) return 'steady';
  const rho = sxy / Math.sqrt(sxx * syy);
  return rho > 0.5 ? 'rising' : (rho < -0.5 ? 'falling' : 'steady');
}

/** Trains / bursts recomputed from the current clicks, counts, and the animal estimate. */
export function recompute(entry, st) {
  const clicks = currentClicks(entry, st);
  const byTrain = new Map();
  clicks.forEach((k) => { if (k.train !== null && ANIMAL_LABELS.has(k.label)) { if (!byTrain.has(k.train)) byTrain.set(k.train, []); byTrain.get(k.train).push(k); } });
  const eng = new Map((entry.trains || []).map((t) => [t.train_id, t]));
  const unmarked = new Set(st.bursts.unmarked);
  const trains = [...byTrain.entries()].map(([id, ks]) => {
    const e = eng.get(id) || {};
    const ts = ks.map((k) => k.t);
    const ici = ts.slice(1).map((t, j) => (t - ts[j]) * 1000);
    const m = median(ici);
    const mean = ici.length ? ici.reduce((p, q) => p + q, 0) / ici.length : null;
    const sd = ici.length > 1 ? Math.sqrt(ici.reduce((p, q) => p + (q - mean) ** 2, 0) / (ici.length - 1)) : null;
    let type = (st.trainType || {})[id] || e.type || 'click train';
    if (type === 'burst pulse' && unmarked.has(id)) type = 'click train';
    return { id, type, origin: e.train_id === undefined ? 'added' : 'engine', animal: e.individual ?? ks[0].ind, n: ks.length,
      start: r3(ts[0]), end: r3(ts[ts.length - 1]), dur: r3(ts[ts.length - 1] - ts[0]), iciMedian: m && r3(m), iciCv: mean && sd ? r3(sd / mean) : null,
      trend: trend(ici), rhythm: e.rhythm_pattern || '', engineN: e.n_clicks ?? null, clicks: ks.map((k) => k.no), ts };
  }).sort((a, b) => a.start - b.start);
  // bursts the person marked: clicks inside the range (animal labels) form a burst
  st.bursts.marked.forEach((b) => {
    const ks = clicks.filter((k) => k.t >= b.t0 && k.t <= b.t1 && ANIMAL_LABELS.has(k.label));
    const ts = ks.map((k) => k.t); const ici = ts.slice(1).map((t, j) => (t - ts[j]) * 1000);
    trains.push({ id: b.id, type: 'burst pulse', origin: 'added', animal: null, n: ks.length, start: r3(b.t0), end: r3(b.t1), dur: r3(b.t1 - b.t0),
      iciMedian: median(ici) && r3(median(ici)), iciCv: null, trend: trend(ici), rhythm: '', engineN: null, clicks: ks.map((k) => k.no), ts });
  });
  trains.sort((a, b) => a.start - b.start);
  // the engine's best estimate links trains across pauses (level, rhythm, spectrum); that is re-run by the engine on
  // the saved review. Here: trains that overlap in time now (>= 3 clicks) — they must be different animals.
  const real = trains.filter((t) => t.n >= 3 && t.type === 'click train');
  let overlap = real.length ? 1 : 0;
  real.forEach((a) => { const at = real.filter((b) => b.start <= a.start && b.end >= a.start).length; if (at > overlap) overlap = at; });
  const counts = {
    animalClicks: clicks.filter((k) => ANIMAL_LABELS.has(k.label)).length,
    inTrains: clicks.filter((k) => k.train !== null && ANIMAL_LABELS.has(k.label)).length,
    trains: trains.filter((t) => t.type === 'click train').length,
    bursts: trains.filter((t) => t.type === 'burst pulse').length,
    echoes: clicks.filter((k) => k.label === 'echo').length,
    other: clicks.filter((k) => !ANIMAL_LABELS.has(k.label) && k.label !== 'echo').length,
    added: st.added.length, removed: st.removed.length,
    animalsBest: entry.summary?.animals_best ?? null, overlapNow: overlap, animalsMax: entry.summary?.animals_max ?? null, animalsEngine: entry.summary?.animals_estimate ?? null,
  };
  return { clicks, trains, counts };
}

// codas: clicks inside a marked range → count, intervals and a rhythm string ("1+1+3": gaps > 1.5x the shortest
// interval split the coda into groups), Rendell & Whitehead style
export function codaOf(clicks, c) {
  const ts = clicks.filter((k) => k.t >= c.t0 && k.t <= c.t1 && ANIMAL_LABELS.has(k.label)).map((k) => k.t);
  const ici = ts.slice(1).map((t, j) => Math.round((t - ts[j]) * 1000));
  const lo = ici.length ? amin(ici) : 0;
  const groups = []; let g = 1;
  ici.forEach((v) => { if (v > 1.5 * lo) { groups.push(g); g = 1; } else g += 1; });
  if (ts.length) groups.push(g);
  return { n: ts.length, ici, rhythm: groups.join('+'), dur: ts.length > 1 ? r3(ts[ts.length - 1] - ts[0]) : 0 };
}

export function toPulsedDoc(entry, st, section, meta = {}, decision = { status: 'confirmed' }) {
  const { clicks, trains, counts } = recompute(entry, st);
  const signal = 'pulsed';
  const engineSp = entry.speciesId || null;
  const finalSp = st.label.speciesId || engineSp;
  const moveTo = st.label.speciesId && st.label.speciesId !== engineSp ? sectionForSpecies(st.label.speciesId, signal) : null;
  const engLabel = (no) => entry.clicks.label[entry.clicks.no.indexOf(Number(no))];
  const engTrain = (no) => entry.clicks.train[entry.clicks.no.indexOf(Number(no))];
  return {
    schema: PULSED_SCHEMA,
    itemId: meta.itemId || null, pieceId: entry.id, section: section.key,
    status: decision.status,
    dismiss: decision.status === 'dismissed' ? { reason: decision.reason, note: decision.note || '' } : null,
    species: { engine: { id: engineSp, label: entry.species || null, source: entry.speciesSource || 'engine' },
      final: { id: finalSp, label: speciesName(finalSp) || entry.species || null, source: st.label.speciesId && st.label.speciesId !== engineSp ? 'person' : (entry.speciesSource || 'engine') },
      moveTo: moveTo && moveTo !== section.key ? moveTo : null },
    label: { codeSystem: section.codeSystem, code: st.label.code, engineCode: entry.code || null },
    frame: { img: entry.img, x0: entry.x0, x1: entry.x1, fmin: 0, fmax: entry.fmax, sampleRate: entry.sampleRate },
    edits: {
      removedClicks: st.removed.map((no) => ({ no, t: entry.clicks.t[entry.clicks.no.indexOf(no)], engineLabel: engLabel(no) })),
      addedClicks: st.added.map((a) => ({ t: a.t, f: a.f, train: a.train ?? null, found: a.auto ? 'find missing pulses' : 'by hand', measured: a.meas || null })),
      relabelled: Object.entries(st.relabel).map(([no, to]) => ({ no: Number(no), from: engLabel(no), to })),
      trainChanged: Object.entries(st.train).map(([no, to]) => ({ no: Number(no), from: engTrain(no), to })),
      trainTypes: Object.entries(st.trainType || {}).map(([id, type]) => ({ id: Number(id), type, new: !(entry.trains || []).some((t) => t.train_id === Number(id)) })),
      burstsUnmarked: st.bursts.unmarked, burstsMarked: st.bursts.marked.map(({ t0, t1 }) => ({ t0, t1 })),
      creaks: st.creaks.map(({ t0, t1 }) => ({ t0, t1 })),
      codas: st.codas.map((c) => ({ t0: c.t0, t1: c.t1, ...codaOf(clicks, c) })),
      localisation: localisationDoc(entry, st)?.decisions || [],
    },
    result: {                                           // recomputed after the edits (light; the engine redoes it in full)
      counts,
      trains: trains.map(({ ts, ...t }) => t),
      engine: { summary: entry.summary || {}, trains: (entry.trains || []).length, animals: entry.summary?.animals_estimate ?? null },
      localisation: localisationDoc(entry, st)?.result || null,
    },
    source: { stationId: meta.stationId || null, fileId: meta.fileId || null, scope: meta.scope || null, bundleKey: meta.bundleKey || null,
      engineVersion: meta.engineVersion || null },
    by: meta.uid || null,
    secondsOpen: Math.round((Date.now() - (st.openedAt || Date.now())) / 1000),
  };
}

/** A saved pulsed review back into the editor (approval step). */
export function fromPulsedDoc(entry, doc) {
  const s = fromPiece(entry);
  const e = doc.edits || {};
  s.removed = (e.removedClicks || []).map((r) => r.no);
  s.added = (e.addedClicks || []).map((a, j) => ({ id: `a${j}`, t: a.t, f: a.f, train: a.train ?? null, auto: a.found === 'find missing pulses', meas: a.measured || null }));
  (e.relabelled || []).forEach((r) => { s.relabel[r.no] = r.to; });
  (e.trainChanged || []).forEach((r) => { s.train[r.no] = r.to; });
  s.trainType = Object.fromEntries((e.trainTypes || []).map((r) => [r.id, r.type]));
  s.bursts = { unmarked: [...(e.burstsUnmarked || [])], marked: (e.burstsMarked || []).map((b, j) => ({ id: `burst${j}`, ...b })) };
  s.creaks = (e.creaks || []).map((b, j) => ({ id: `creak${j}`, t0: b.t0, t1: b.t1 }));
  s.codas = (e.codas || []).map((b, j) => ({ id: `coda${j}`, t0: b.t0, t1: b.t1 }));
  s.loc = Object.fromEntries((e.localisation || []).map((r) => [r.no, r.decision]));
  s.label = { ...s.label, code: doc.label?.code || s.label.code, speciesId: doc.species?.final?.id || s.label.speciesId };
  return s;
}

/** Put pulses into train / burst `id` (null = take them out). A pulse the engine had left as shrimp / uncertain / echo
 * … becomes an animal click when the reviewer puts it in a train (only animal clicks count in trains); taking it out
 * again gives it back its engine label. Works on the state `s` inside change(). */
export function setPulsesTrain(s, entry, nos, id) {
  const lab = (no) => entry.clicks.label[entry.clicks.no.indexOf(no)];
  nos.forEach((no) => {
    const a = s.added.find((x) => x.id === no);
    if (a) { a.train = id; return; }
    if (id === null) { s.train[no] = null; if (s.relabel[no] === 'sequence' && !ANIMAL_LABELS.has(lab(no))) delete s.relabel[no]; return; }
    s.train[no] = id;
    const now = s.relabel[no] || lab(no);
    if (!ANIMAL_LABELS.has(now)) s.relabel[no] = 'sequence';
  });
}

// pulses closer than this to a click are its other arrivals (surface / bottom echoes 0.1–5 ms after it); dolphin
// click intervals are longer (bursts ≥ ~2 ms are handled by the engine, not by hand-joining)
export const ECHO_GAP_S = 0.005;
// ... but only for click trains: burst pulses can have intervals of 2–5 ms themselves
export const ECHO_CHECK_MIN_ICI_S = 0.015;
const medianIci = (ts) => { const s = [...ts].sort((a, b) => a - b); const iv = s.slice(1).map((t, j) => t - s[j]).sort((a, b) => a - b); return iv.length ? iv[Math.floor(iv.length / 2)] : null; };
/** true when the train's median interval is long enough that a pulse < 5 ms from a click must be an echo */
export function echoCheckApplies(clicks, trainId) {
  const m = medianIci(clicks.filter((k) => k.train === trainId && ANIMAL_LABELS.has(k.label)).map((k) => k.t));
  return m !== null && m >= ECHO_CHECK_MIN_ICI_S;
}
/** the train pulse within ECHO_GAP_S of time t (another arrival of that click), or null */
export function echoPartner(clicks, trainId, t, no = null) {
  if (!echoCheckApplies(clicks, trainId)) return null;
  return clicks.find((k) => k.train === trainId && ANIMAL_LABELS.has(k.label) && k.no !== no && Math.abs(k.t - t) < ECHO_GAP_S) || null;
}

/** Extend / shorten train `id` to the span [a, b] (user dragged a bar handle, 7 Oct 2026).
 * - other trains / bursts overlapping the new span are merged into it whole (one train or burst, one colour);
 * - free pulses (no train, or left by the engine as shrimp / uncertain / …) join ONLY if they continue the rhythm and
 *   level, walking outward from the train's ends like the engine's tracker (technical reference §6, "Continue":
 *   interval 0.75–1.33× the median of the last 4, level within 4 dB) — here a little looser (0.7–1.45×, 5 dB) and one
 *   missed pulse allowed (1.6–2.4×), because the spectrum is not compared in the browser;
 * - the train's pulses outside [a, b] are let go.
 * Returns { take, merge, drop, left } (pulse nos, train ids, pulse nos, free pulses in the span not taken). */
export function extendTrain(clicks, id, a, b) {
  const NOT = new Set(['echo', 'interference', 'machinery', 'vessel']);
  const isFree = (k) => !NOT.has(k.label) && (k.train === null || !ANIMAL_LABELS.has(k.label));
  const own = clicks.filter((k) => k.train === id && ANIMAL_LABELS.has(k.label) && k.t >= a && k.t <= b);
  const drop = clicks.filter((k) => k.train === id && (k.t < a || k.t > b)).map((k) => k.no);
  // whole trains overlapping the new span
  const spans = new Map();
  clicks.forEach((k) => { if (k.train === null || k.train === id || !ANIMAL_LABELS.has(k.label) || typeof k.train !== 'number') return;
    const s = spans.get(k.train) || [Infinity, -Infinity]; spans.set(k.train, [Math.min(s[0], k.t), Math.max(s[1], k.t)]); });
  const ownTimes = own.map((k) => k.t);
  const isReflectionOf = (tid) => {                 // most of its pulses 0.1–5 ms from one of ours
    const ks = clicks.filter((k) => k.train === tid && ANIMAL_LABELS.has(k.label));
    const near = ks.filter((k) => ownTimes.some((t) => Math.abs(k.t - t) < ECHO_GAP_S && Math.abs(k.t - t) > 0.0001)).length;
    return ks.length && near >= 0.5 * ks.length;
  };
  const spanTrains = [...spans.entries()].filter(([, [s, e]]) => e >= a && s <= b).map(([tid]) => tid);
  const merge = spanTrains.filter((tid) => !isReflectionOf(tid));
  const reflections = spanTrains.filter((tid) => isReflectionOf(tid));
  const mergedPulses = clicks.filter((k) => merge.includes(k.train) && ANIMAL_LABELS.has(k.label));
  let seq = [...own, ...mergedPulses].sort((p, q) => p.t - q.t);
  const free = clicks.filter((k) => isFree(k) && k.t >= a && k.t <= b && !seq.some((s) => s.no === k.no)).sort((p, q) => p.t - q.t);
  const take = [];
  const used = new Set();
  const lvl = (k) => (Number.isFinite(k.level) ? k.level : null);
  // a pulse within ECHO_GAP_S of a pulse already in the train is another arrival of that click (surface / bottom
  // echo), not the next click (9 Oct 2026): never taken in
  const seqMed = medianIci(seq.map((q) => q.t));
  const nearSeq = (k) => seqMed !== null && seqMed >= ECHO_CHECK_MIN_ICI_S && seq.some((q) => Math.abs(q.t - k.t) < ECHO_GAP_S);
  // direct sound first (9 Oct 2026): a pulse with a free pulse up to 5 ms EARLIER that is at least as loud is that
  // pulse's echo — take the earlier one (otherwise the walk steps onto the reflections and follows them)
  const direct = (k) => {
    if (seqMed === null || seqMed < ECHO_CHECK_MIN_ICI_S || lvl(k) === null) return k;
    const e = free.filter((q) => !used.has(q.no) && q.t < k.t && k.t - q.t < ECHO_GAP_S && lvl(q) !== null && lvl(q) >= lvl(k) - 1 && !nearSeq(q));
    return e.length ? e.reduce((a, q) => (q.t < a.t ? q : a)) : k;
  };
  // walk in one direction (+1 forward from the last pulse, -1 backward from the first); also fills gaps inside
  const walk = (dir) => {
    for (let guard = 0; guard < 5000; guard += 1) {
      const ts = seq.map((k) => k.t);
      const end = dir > 0 ? seq[seq.length - 1] : seq[0];
      const ivs = ts.slice(1).map((t, j) => t - ts[j]);
      const recent = dir > 0 ? ivs.slice(-4) : ivs.slice(0, 4);
      const I = recent.length ? [...recent].sort((p, q) => p - q)[Math.floor(recent.length / 2)] : null;
      let best = null; let bc = Infinity;
      free.forEach((k) => {
        if (used.has(k.no) || nearSeq(k)) return;
        const gap = dir > 0 ? k.t - end.t : end.t - k.t; if (gap <= 0) return;
        const r = I ? gap / I : 1; const dL = lvl(k) !== null && lvl(end) !== null ? Math.abs(lvl(k) - lvl(end)) : 0;
        if (dL > 5) return;
        let cost;
        if (r >= 0.7 && r <= 1.45) cost = Math.abs(Math.log(r)) + dL / 8;
        else if (r >= 1.6 && r <= 2.4) cost = Math.abs(Math.log(r / 2)) + dL / 8 + 0.5;
        else if (!I && gap <= 0.25) cost = dL / 8;                 // a train of one pulse: the nearest pulse of similar level
        else return;
        if (cost < bc) { bc = cost; best = k; }
      });
      if (!best) return;
      best = direct(best);
      used.add(best.no); take.push(best.no);
      seq = dir > 0 ? [...seq, best] : [best, ...seq];
    }
  };
  // gaps inside (e.g. between the train and a train merged into it): walk forward from the pulse before the gap
  const fillGaps = () => {
    for (let pass = 0; pass < 200; pass += 1) {
      const ts = seq.map((k) => k.t); const ivs = ts.slice(1).map((t, j) => t - ts[j]);
      const med = ivs.length ? [...ivs].sort((p, q) => p - q)[Math.floor(ivs.length / 2)] : null; if (!med) return;
      let filled = false;
      for (let j = 0; j < ivs.length && !filled; j += 1) {
        if (ivs[j] < 1.6 * med) continue;
        const left = seq[j]; const right = seq[j + 1];
        const loc = ivs.slice(Math.max(0, j - 4), j).filter((v) => v < 1.6 * med); const I = loc.length ? [...loc].sort((p, q) => p - q)[Math.floor(loc.length / 2)] : med;
        let best = null; let bc = Infinity;
        free.forEach((k) => {
          if (used.has(k.no) || nearSeq(k) || k.t <= left.t || k.t >= right.t - 0.5 * I) return;
          const r = (k.t - left.t) / I; const dL = lvl(k) !== null && lvl(left) !== null ? Math.abs(lvl(k) - lvl(left)) : 0; if (dL > 5) return;
          let cost; if (r >= 0.7 && r <= 1.45) cost = Math.abs(Math.log(r)) + dL / 8; else if (r >= 1.6 && r <= 2.4) cost = Math.abs(Math.log(r / 2)) + dL / 8 + 0.5; else return;
          if (cost < bc) { bc = cost; best = k; } });
        if (best) { best = direct(best); used.add(best.no); take.push(best.no); seq = [...seq, best].sort((p, q) => p.t - q.t); filled = true; }
      }
      if (!filled) return;
    }
  };
  if (seq.length) { walk(1); walk(-1); fillGaps(); }
  const leftNos = free.filter((k) => !used.has(k.no)).map((k) => k.no);
  return { take, merge, reflections, mergedPulses: mergedPulses.map((k) => k.no), drop, left: leftNos.length, leftNos };
}

/** Which arrival is the click (9 Oct 2026). Sound takes the shortest path first, so of two arrivals of one click the
 * EARLIER is the click and the later, weaker one its echo (surface / bottom reflection, here 0.1–5 ms later).
 * For pulse k near a pulse of train `trainId`: null, or { mate, dtMs (k − mate), dB (k − mate), kind }:
 *  'echo'    k comes after the train pulse → k is its echo;
 *  'direct'  k comes before and is at least as loud (−1 dB) → k is the click and the train pulse its echo (swap);
 *  'earlier' k comes before but is weaker → unclear (an off-axis direct sound or another pulse). */
export function arrivalCheck(clicks, trainId, k) {
  if (!echoCheckApplies(clicks, trainId)) return null;
  const near = clicks.filter((q) => q.train === trainId && ANIMAL_LABELS.has(q.label) && q.no !== k.no && Math.abs(q.t - k.t) < ECHO_GAP_S);
  if (!near.length) return null;
  const mate = near.reduce((a, q) => (Math.abs(q.t - k.t) < Math.abs(a.t - k.t) ? q : a));
  const dtMs = (k.t - mate.t) * 1000;
  const dB = Number.isFinite(k.level) && Number.isFinite(mate.level) ? k.level - mate.level : null;
  const kind = dtMs > 0 ? 'echo' : (dB === null || dB >= -1 ? 'direct' : 'earlier');
  return { mate, dtMs, dB, kind };
}

/** The click a pulse is an echo of: the engine's link when it has one, else the nearest LOUDER pulse 0.1–5 ms before
 * it (only for pulses labelled echo, or not in a train). Returns { parent, dtMs, dB } or null. */
export function echoParent(clicks, k, byNo = null) {
  if (k.train !== null && ANIMAL_LABELS.has(k.label)) return null;
  const m = byNo || new Map(clicks.map((q) => [q.no, q]));
  let parent = typeof k.echoOf === 'number' && k.echoOf > 0 ? m.get(k.echoOf) : null;
  if (!parent) {
    const lo = k.t - ECHO_GAP_S;
    const c = clicks.filter((q) => q.t < k.t - 0.0001 && q.t >= lo && Number.isFinite(q.level) && Number.isFinite(k.level) && q.level >= k.level + 3);
    if (!c.length) return null;
    // prefer a train click, then the loudest
    parent = c.reduce((a, q) => { const ta = a.train !== null && ANIMAL_LABELS.has(a.label); const tq = q.train !== null && ANIMAL_LABELS.has(q.label);
      return tq && !ta ? q : (ta && !tq ? a : (q.level > a.level ? q : a)); });
  }
  return { parent, dtMs: (k.t - parent.t) * 1000, dB: Number.isFinite(k.level) && Number.isFinite(parent.level) ? k.level - parent.level : null };
}

/** Pulses of train `id` that are probably ECHOES of a louder pulse 0.15–5 ms before them that is not in the train
 * (the train was built on the reflections). Returns [{ echo, click }] — swap: click into the train, echo out. */
export function echoesInTrain(clicks, id) {
  if (!echoCheckApplies(clicks, id)) return [];
  const own = clicks.filter((q) => q.train === id && ANIMAL_LABELS.has(q.label));
  const taken = new Set();
  const out = [];
  own.forEach((k) => {
    if (!Number.isFinite(k.level)) return;
    const c = clicks.filter((q) => q.train !== id && !taken.has(q.no) && q.t < k.t - 0.00015 && k.t - q.t < ECHO_GAP_S && Number.isFinite(q.level)
      && q.level >= k.level + 3 && !['interference', 'machinery', 'vessel'].includes(q.label) && !(q.train !== null && ANIMAL_LABELS.has(q.label)));
    if (!c.length) return;
    const click = c.reduce((a, q) => (q.t < a.t ? q : a));      // the earliest = the direct sound
    taken.add(click.no); out.push({ echo: k, click });
  });
  return out;
}

/** Put `clickNo` into train `id` and turn `echoNo` (now in the train) into an echo of it. */
export function swapArrivals(s, entry, clickNo, echoNo, id) {
  setPulsesTrain(s, entry, [echoNo], null);
  setPulsesTrain(s, entry, [clickNo], id);
  if (!s.added.some((a) => a.id === echoNo)) s.relabel[echoNo] = 'echo';
}


/** Usual ECHOLOCATION click-interval range per section (s), drawn as a light band in the interval panel so that
 * intervals outside it stand out (an echo inside a train, a burst, a buzz). Approximate, from the literature
 * (10 Oct 2026): delphinids — bottlenose train clicks 80 ± 2 ms, burst pulses 3.4 ms (Papale et al., central
 * Mediterranean; burst pulses < 10 ms is our boundary); Cuvier's beaked whale 0.40–0.43 s (± 0.07–0.09; Zimmer et
 * al. 2005); harbour porpoise / NBHF echolocation > 15 ms, buzzes and calls below (Sørensen et al. 2018); sperm whale
 * usual clicks 0.4–2.5 s (engine click_fingerprints.json). */
export const USUAL_ICI = {
  dolphin: { range: [0.015, 0.15], text: 'usual echolocation range for dolphins (about 15–150 ms)' },
  beaked_whale: { range: [0.2, 0.6], text: 'usual echolocation range for beaked whales (about 0.2–0.6 s)' },
  porpoise_nbhf: { range: [0.015, 0.15], text: 'usual echolocation range for porpoises (above 15 ms)' },
  sperm_whale: { range: [0.4, 2.5], text: 'usual clicks of sperm whales (0.4–2.5 s)' },
};
export const usualIci = (sectionKey) => USUAL_ICI[sectionKey] || USUAL_ICI.dolphin;

/** Every change of a review, grouped by kind: [kind, [[description, undo(s)], …]] — for "Your changes" and the
 * summary before saving */
export function changeGroups(c, st) {
  const eng = (no) => c.clicks.label[c.clicks.no.indexOf(Number(no))];
  const auto = (no) => st.relabel[no] === 'sequence' && st.train[no] !== null && st.train[no] !== undefined && !ANIMAL_LABELS.has(eng(no));
  const backTrain = (no) => (s) => { delete s.train[no]; if (s.relabel[no] === 'sequence' && !ANIMAL_LABELS.has(eng(no))) delete s.relabel[no]; };
  const tIn = Object.entries(st.train).filter(([, t]) => t !== null);
  const tOut = Object.entries(st.train).filter(([, t]) => t === null);
  const tOf = (no) => c.clicks.t[c.clicks.no.indexOf(no)];
  return [
    ['Put into a train / burst', tIn.map(([no, t]) => [`#${no} → #${t}${auto(no) ? ' (now an animal click)' : ''}`, backTrain(no)])],
    ['Taken out of a train / burst', tOut.map(([no]) => [`#${no}`, backTrain(no)])],
    ['Added pulses', st.added.map((x) => [`${x.t} s${x.train !== null && x.train !== undefined ? ` · #${x.train}` : ''}${x.auto ? ' (found)' : ''}`, (s) => { s.added = s.added.filter((y) => y.id !== x.id); }])],
    ['Removed clicks', st.removed.map((no) => [`#${no} at ${Number.isFinite(tOf(no)) ? tOf(no).toFixed(3) : '?'} s`, (s) => { s.removed = s.removed.filter((y) => y !== no); }])],
    ['Relabelled', Object.entries(st.relabel).filter(([no]) => !auto(no)).map(([no, l]) => [`#${no} → ${LABEL[l]?.[0] || l}`, (s) => { delete s.relabel[no]; }])],
    ['Train / burst type changed', Object.entries(st.trainType || {}).map(([id, t]) => [`#${id} → ${t}`, (s) => { delete s.trainType[id]; }])],
    ['Not a burst', st.bursts.unmarked.map((id) => [`train #${id}`, (s) => { s.bursts.unmarked = s.bursts.unmarked.filter((x) => x !== id); }])],
    ['Bursts marked', st.bursts.marked.map((x) => [`${x.t0}–${x.t1} s`, (s) => { s.bursts.marked = s.bursts.marked.filter((y) => y.id !== x.id); }])],
    ['Creaks', st.creaks.map((x) => [`${x.t0}–${x.t1} s`, (s) => { s.creaks = s.creaks.filter((y) => y.id !== x.id); }])],
    ['Codas', st.codas.map((x) => [`${x.t0}–${x.t1} s`, (s) => { s.codas = s.codas.filter((y) => y.id !== x.id); }])],
  ].filter(([, xs]) => xs.length);
}

/** play [a, b] s of a signal, `slow` times slower (dolphin clicks are ultrasonic: 8× brings 20–80 kHz to 2.5–10 kHz) */
let audioCtx = null;
export function playSegment(sig, a, b, slow = 1) {
  if (!sig) return null;
  const y = sig.x || sig.y; const fs = sig.fs;
  const i0 = Math.max(0, Math.floor(a * fs)); const i1 = Math.min(y.length, Math.ceil(b * fs));
  if (i1 - i0 < 16) return null;
  const rate = Math.max(3000, Math.min(768000, Math.round(fs / slow)));
  audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
  const buf = audioCtx.createBuffer(1, i1 - i0, rate);
  const ch = buf.getChannelData(0); let mx = 1e-9;
  for (let i = i0; i < i1; i += 1) mx = Math.max(mx, Math.abs(y[i]));
  for (let i = i0; i < i1; i += 1) ch[i - i0] = (y[i] / mx) * 0.9;
  const src = audioCtx.createBufferSource(); src.buffer = buf; src.connect(audioCtx.destination); src.start();
  return { seconds: (i1 - i0) / rate, stop: () => { try { src.stop(); } catch (e) { /* ended */ } } };
}
